"use client"

import { useState } from "react"
import Image from "next/image"
import Link from "next/link"
import { ArrowLeft, Loader2, LogOut, User } from "lucide-react"

import { createClient } from "@/lib/supabase/client"

interface SalesHeaderProps {
  courseTitle: string
  isLoggedIn: boolean
  role: string | null
  firstName: string | null
  avatarUrl: string | null
}

export function SalesHeader({
  courseTitle,
  isLoggedIn,
  role,
  firstName,
  avatarUrl,
}: SalesHeaderProps) {
  const [showMenu, setShowMenu] = useState(false)
  const [signingOut, setSigningOut] = useState(false)

  const backHref =
    role === "admin" ? "/admin" : role === "partner" ? "/partners" : "/courses"
  const initials = firstName?.charAt(0).toUpperCase() || "?"

  async function handleSignOut() {
    setSigningOut(true)
    await createClient().auth.signOut()
    window.location.href = "/"
  }

  return (
    <header className="sticky top-0 z-30 border-b border-sales-line bg-[rgba(250,250,249,.88)] backdrop-blur-[12px]">
      <div className="mx-auto flex h-[58px] w-full max-w-[1180px] items-center justify-between px-5 sm:px-8 lg:px-10">
        <div className="flex min-w-0 items-center gap-3">
          {/* Magenta de identidade (primary-500) — só no logo, nunca em botão */}
          <Link
            href="/"
            className="font-heading text-[21px] leading-none font-extrabold tracking-[-0.045em] text-primary-500"
          >
            dzp.
          </Link>
          <span className="hidden h-4 w-px bg-sales-line-strong md:block" />
          <span className="hidden truncate text-[13px] text-sales-text md:block">
            {courseTitle}
          </span>
        </div>

        <div className="flex items-center gap-3">
          <a
            href="#conteudo"
            className="hidden text-[13px] font-medium text-sales-text transition-colors hover:text-sales-ink sm:block"
          >
            Conteúdo
          </a>
          <a
            href="#oferta"
            className="hidden text-[13px] font-medium text-sales-text transition-colors hover:text-sales-ink sm:block"
          >
            Investimento
          </a>
          <a
            href="#oferta"
            className="rounded-lg bg-primary-600 px-4 py-2 text-[13px] font-semibold text-white transition-transform active:scale-[0.98]"
          >
            Matricular
          </a>

          {isLoggedIn && (
            <div className="relative">
              <button
                onClick={() => setShowMenu(!showMenu)}
                aria-label="Menu do usuário"
                className="flex items-center rounded-lg p-1 transition-colors hover:bg-sales-bg-alt"
              >
                {avatarUrl ? (
                  <Image
                    src={avatarUrl}
                    alt="Foto de perfil"
                    referrerPolicy="no-referrer"
                    width={30}
                    height={30}
                    className="rounded-full object-cover"
                  />
                ) : (
                  <span className="flex h-[30px] w-[30px] items-center justify-center rounded-full bg-primary-app-500 text-[13px] font-medium text-white">
                    {initials}
                  </span>
                )}
              </button>

              {showMenu && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowMenu(false)}
                  />
                  <div className="absolute top-full right-0 z-20 mt-2 w-52 rounded-xl border border-sales-line bg-white py-2 shadow-lg">
                    <Link
                      href={backHref}
                      className="flex items-center gap-3 px-4 py-2 text-[13px] text-sales-ink-soft hover:bg-sales-bg-alt"
                      onClick={() => setShowMenu(false)}
                    >
                      <ArrowLeft className="h-4 w-4" />
                      Voltar ao painel
                    </Link>
                    <Link
                      href="/perfil"
                      className="flex items-center gap-3 px-4 py-2 text-[13px] text-sales-ink-soft hover:bg-sales-bg-alt"
                      onClick={() => setShowMenu(false)}
                    >
                      <User className="h-4 w-4" />
                      Meu Perfil
                    </Link>
                    <hr className="my-2 border-sales-line" />
                    <button
                      onClick={handleSignOut}
                      disabled={signingOut}
                      className="flex w-full items-center gap-3 px-4 py-2 text-[13px] text-error hover:bg-sales-bg-alt disabled:opacity-50"
                    >
                      {signingOut ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <LogOut className="h-4 w-4" />
                      )}
                      {signingOut ? "Saindo..." : "Sair"}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  )
}
